import { useEffect, useState } from 'react';

import {
	AdminPanelSettings as AdminPanelSettingsIcon,
	Block as BlockIcon,
	DeleteOutlined as DeleteOutlinedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import { ConfirmationDialog, FormDialog } from '~components';
import { useAuthorize } from '~features/authentication';
import { BillingCard } from '~features/finances';
import { NfcGeneralInfoCard, NfcTag, NfcTagsService } from '~features/nfc';
import { useSnackbar } from '~hooks';
import { DetailsGridLayout, usePageLayoutProperties } from '~layouts';

import UserGeneralInfoCard from '../components/cards/userGeneralInfoCard';
import UserLicenseCard from '../components/cards/userLicenseCard';
import UserOrganisationInfoCard from '../components/cards/userOrganisationInfoCard';
import LinkNfcTagToUserForm from '../forms/linkNfcTagToUser/linkNfcTagToUserForm';
import UsersService from '../services/usersService';

const service = new UsersService();
const nfcTagsService = new NfcTagsService();

type UserAction =
	| {
			type: 'link';
			id?: never;
	  }
	| {
			type: 'unlink';
			id: string;
	  };

interface UserDetailsContainerProps {
	userId: string;
}

const UserDetailsContainer = ({ userId }: UserDetailsContainerProps) => {
	const { t } = useTranslation();
	const { setPageHeaderButtons: setPageButtons } = usePageLayoutProperties();
	const { enqueueSuccessSnackbar, enqueueAxiosErrorSnackbar } = useSnackbar();
	const { isSuperAdmin } = useAuthorize();

	const [userAction, setUserAction] = useState<UserAction | null>(null);

	const { trigger: linkNfcTrigger, isMutating: isLinkNfcMutating } = useSWRMutation(
		[nfcTagsService.basePath, userId],
		([_, id], { arg }: { arg: NfcTag }) => nfcTagsService.updateNfcTag(id, arg),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(t('linked'));
				setUserAction(null);
				// dataGridRef.current.mutate();
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	const { trigger: unlinkNfcTrigger, isMutating: isUnlinkNfcMutating } = useSWRMutation(
		userAction?.id ? [nfcTagsService.basePath, userAction.id] : undefined,
		([_, id]) => nfcTagsService.unlinkNfcTag(id),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(t('linked'));
				setUserAction(null);
				// dataGridRef.current.mutate();
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	const { data } = useSWR([service.basePath, userId], ([_, args]) => service.getUserById(args));

	useEffect(() => {
		if (isSuperAdmin()) {
			setPageButtons([
				{
					children: t('changeRole'),
					startIcon: <AdminPanelSettingsIcon />,
				},
				{
					children: t('deactivate'),
					startIcon: <BlockIcon />,
					color: 'error',
				},
				{
					children: t('delete'),
					startIcon: <DeleteOutlinedIcon />,
					color: 'error',
				},
			]);
		}
	}, [isSuperAdmin, setPageButtons, t]);

	return (
		<>
			<DetailsGridLayout>
				<UserGeneralInfoCard userId={userId} />
				<UserOrganisationInfoCard userId={userId} />
				<BillingCard
				// value={{
				// 	address: data?.address,
				// 	...data?.payment,
				// }}
				/>
				{isSuperAdmin() && <UserLicenseCard userId={data?.hasUserLicense ? userId : undefined} />}
				<NfcGeneralInfoCard
					id={data?.cards?.[0]?.id}
					organisationId={data?.organisation.id}
					onUnlinkClick={(nfcId) => setUserAction({ type: 'unlink', id: nfcId })}
					onLinkClick={() => setUserAction({ type: 'link' })}
				/>
			</DetailsGridLayout>
			<FormDialog
				open={userAction?.type === 'link'}
				loading={isLinkNfcMutating}
				onClose={() => setUserAction(null)}
			>
				<LinkNfcTagToUserForm userId={userId} onSubmit={async (value) => linkNfcTrigger(value)} />
			</FormDialog>
			<ConfirmationDialog
				open={userAction?.type === 'unlink'}
				loading={isUnlinkNfcMutating}
				title={t('unlink')}
				subTitle={'TODO'}
				onConfirm={async () => unlinkNfcTrigger()}
				onClose={() => setUserAction(null)}
			/>
		</>
	);
};

export default UserDetailsContainer;
